import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../shared/components/Button';
import { ButtonsGroup } from '../../shared/components/ButtonsGroup';
import { OllieToast, OllieToastRef } from '../../shared/components/OllieToast';
import { Radio } from '../../shared/components/Radio';
import { TitleAndSubtitle } from '../../shared/components/TitleAndSubtitle';
import { useLoggedInUser } from '../../shared/hooks/useLoggedInUser';
import { ROUTES } from '../../shared/routing/routes';
import useOcdProfileStore from '../../systems/user-info/useOcdProfileStore';
import { useOnboardingOcdThemesStore } from './hooks/useOnboardingOcdThemesStore';
import { useOnboardingStore } from './hooks/useOnboardingStore';

export const OcdThemesSelection = () => {
    const navigate = useNavigate();
    const user = useLoggedInUser();
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const setCurrentStep = useOnboardingStore(state => state.setCurrentStep);
    const saveOcdInfoFromUser = useOcdProfileStore(state => state.saveOcdInfoFromUser);
    const toastRef = useRef<OllieToastRef>(null);
    const [isLoading, setIsLoading] = useState(false);
    const init = useOnboardingOcdThemesStore(state => state.init);
    const ocdThemes = useOnboardingOcdThemesStore(state => state.ocdThemes);

    useEffect(() => {
        setCurrentStep(6);
    }, [setCurrentStep]);

    useEffect(() => {
        init();
    }, [init]);

    const handleSelect = (id: string) => {
        setSelectedOptions(prev => {
            if (prev.includes(id)) {
                return prev.filter(option => option !== id);
            }

            if (prev.length >= 5) {
                return prev;
            }

            return [...prev, id];
        });
    }

    const handleContinue = async () => {
        setIsLoading(true);
        try {
            const showToast = toastRef.current?.animateIn(true);
            const saveData = saveOcdInfoFromUser(user.uid, 'ocd_types', selectedOptions);
            await Promise.all([showToast, saveData]);
        }
        catch (error) {
            setIsLoading(false);
        }
        finally {
            await toastRef.current?.animateOut();
            navigate(ROUTES.onboarding.ocdThemesDetailsSelect);
        }
    }

    if (!ocdThemes || ocdThemes.length === 0) {
        return null;
    }

    return (
        <div className='flex flex-col gap-4 safe-pb-4 px-8 flex-1 overflow-hidden'>
            <title>Ocd Themes</title>
            <OllieToast ref={toastRef} text='Learning' />
            <div className='flex-shrink-0'>
                <TitleAndSubtitle
                    title='What OCD Theme(s) do you struggle with the most right now?'
                    subtitle='Select all that apply up to 5.' />
            </div>
            <div className='flex-1 overflow-y-auto py-4'>
                <Radio options={ocdThemes.map((theme) => ({
                    id: theme.key,
                    label: theme.friendlyName,
                    state: selectedOptions.includes(theme.key) ? 'selected' : 'notSelected',
                }))}
                    onSelect={handleSelect} />
            </div>
            <ButtonsGroup animated={false}>
                <Button label='Continue' inactive={selectedOptions.length < 1 || isLoading} onClick={handleContinue} />
            </ButtonsGroup>
        </div>
    );
};